import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, Loader2, Package } from 'lucide-react';
import { Difference, Money, Stat } from './money';
import { TIE_OUT_TOLERANCE, type StockTieOutReport } from './model';

interface StockCardProps {
  report: StockTieOutReport | undefined;
  loading: boolean;
  error?: string;
}

/** Trading only: 153* GL balance vs the TRY value of the open FIFO layers. */
export function StockCard({ report, loading, error }: StockCardProps) {
  const { t } = useTranslation('tieOut');
  const tied = report ? Math.abs(report.difference) <= TIE_OUT_TOLERANCE : false;

  return (
    <section className="rounded-md border border-border">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold">
            <Package className="h-4 w-4" />
            {t('stock.title')}
          </h2>
          <p className="text-xs text-muted-foreground">{t('stock.desc')}</p>
        </div>
        {report &&
          (tied ? (
            <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-2 py-1 text-xs font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {t('stock.balanced')}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-md bg-red-100 px-2 py-1 text-xs font-medium text-red-800 dark:bg-red-900/40 dark:text-red-200">
              <AlertTriangle className="h-3.5 w-3.5" />
              {t('stock.unbalanced')}
            </span>
          ))}
      </header>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}
      {error && <p className="px-4 py-3 text-sm text-destructive">{error}</p>}

      {report && (
        <div className="space-y-4 p-4">
          <div className="grid gap-2 sm:grid-cols-3">
            <Stat label={t('stock.gl')} value={<Money value={report.glBalance} />} />
            <Stat label={t('stock.fifo')} value={<Money value={report.fifoValue} />} />
            <Stat label={t('stock.difference')} value={<Difference value={report.difference} />} tone={tied ? 'emerald' : 'red'} />
          </div>

          {report.missingRates.length > 0 && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-100">
              {t('stock.missingRates', { list: report.missingRates.join(', ') })}
            </p>
          )}

          <div className="grid gap-4 lg:grid-cols-2">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t('stock.accounts')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('stock.balance')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.accounts.map((a) => (
                    <tr key={a.id} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2">
                        <span className="font-mono text-xs">{a.code}</span> <span className="text-muted-foreground">{a.name}</span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Money value={a.balance} />
                      </td>
                    </tr>
                  ))}
                  {report.accounts.length === 0 && (
                    <tr>
                      <td colSpan={2} className="px-3 py-3 text-sm text-muted-foreground">
                        {t('stock.noAccounts')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                    <th className="px-3 py-2 font-medium">{t('stock.currency')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('stock.batches')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('stock.quantity')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('stock.value')}</th>
                    <th className="px-3 py-2 text-right font-medium">{t('stock.valueTry')}</th>
                  </tr>
                </thead>
                <tbody>
                  {report.byCurrency.map((c) => (
                    <tr key={c.currency} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-2 font-mono text-xs">{c.currency}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.batches}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{c.quantity}</td>
                      <td className="px-3 py-2 text-right">
                        <Money value={c.value} currency={c.currency} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Money value={c.valueTry} />
                      </td>
                    </tr>
                  ))}
                  {report.byCurrency.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-3 py-3 text-sm text-muted-foreground">
                        {t('stock.noBatches')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
